import { useHideLimitedCompanyShiftFields } from 'ogp/limited-company-capabilities';
import { useMemo } from 'react';
import { FieldError, useFormContext } from 'react-hook-form';
import { WorkFormContext } from '../work-form.types';

type WorkFormConfig = {
  showCompanySelect: boolean | undefined;
  showTemplateSelect: boolean | undefined;
};

const getFieldsWarning = (
  fields: { name: string; value: unknown; error: FieldError | undefined }[]
) => {
  const notSelectedFields = fields.filter((field) => field.value == null);
  const errorredFields = fields.filter((field) => field.error != null);
  const notSelectedFieldsCount = notSelectedFields.length;
  const errorredFieldsCount = errorredFields.length;
  if (notSelectedFieldsCount > 1) {
    return `${notSelectedFieldsCount} missende velden.`;
  }
  if (errorredFieldsCount > 1) {
    return `${errorredFieldsCount} foutieve velden.`;
  }
  if (notSelectedFieldsCount === 1) {
    return `${notSelectedFields[0].name} ontbreekt.`;
  }
  if (errorredFieldsCount === 1) {
    return errorredFields[0].error?.message || `${errorredFields[0].name} is foutief.`;
  }
};

export const useWorkFormWarnings = ({ showCompanySelect, showTemplateSelect }: WorkFormConfig) => {
  const hideLimitedCompanyShiftFields = useHideLimitedCompanyShiftFields();
  const { watch, formState } = useFormContext<WorkFormContext>();
  const isCompanySelected = watch('companyOption') != null || !showCompanySelect;
  const isWorkTemplateSelected = watch('jobId') != null;

  const selectedDepartment = watch('department');
  const departmentError = formState.errors.department;
  const selectedStartDate = watch('startDate');
  const startDateError = formState.errors.startDate;
  const selectedEndDate = watch('endDate');
  const endDateError = formState.errors.endDate;
  const selectedStartTime = watch('startTime');
  const startTimeError = formState.errors.startTime;
  const selectedEndTime = watch('endTime');
  const endTimeError = formState.errors.endTime;

  const workInfo = useMemo(() => {
    if (!isCompanySelected && showCompanySelect) {
      return 'Selecteer een bedrijf.';
    }
    if (isCompanySelected && !isWorkTemplateSelected && showTemplateSelect) {
      return 'Selecteer een functie.';
    }
    const fields = [
      ...(hideLimitedCompanyShiftFields
        ? []
        : [
            {
              name: 'Afdeling',
              value: selectedDepartment,
              error: departmentError,
            },
          ]),
      {
        name: 'Startdatum',
        value: selectedStartDate,
        error: startDateError,
      },
      {
        name: 'Einddatum',
        value: selectedEndDate,
        error: endDateError,
      },
      {
        name: 'Van tijd',
        value: selectedStartTime,
        error: startTimeError,
      },
      {
        name: 'Tot tijd',
        value: selectedEndTime,
        error: endTimeError,
      },
    ];

    return getFieldsWarning(fields);
  }, [
    isCompanySelected,
    isWorkTemplateSelected,
    selectedDepartment,
    selectedStartDate,
    selectedEndDate,
    selectedStartTime,
    selectedEndTime,
    departmentError,
    startDateError,
    endDateError,
    startTimeError,
    endTimeError,
    hideLimitedCompanyShiftFields,
  ]);

  const selectedBreakMinutes = watch('breakMinutes');
  const breakMinutesError = formState.errors.breakMinutes;
  const selectedMaxClaimants = watch('maxClaimants');
  const maxClaimantsError = formState.errors.maxClaimants;
  const selectedIsFreelance = watch('isFreelance');
  const isFreelanceError = formState.errors.isFreelance;
  const selectedHourlyRate = watch('hourlyRate');
  const hourlyRateError = formState.errors.hourlyRate;
  const selectedMutableUntilHours = watch('mutableUntilHours');
  const mutableUntilHoursError = formState.errors.mutableUntilHours;
  const selectedIsTempwork = watch('isTempwork');
  const isTempworkError = formState.errors.isTempwork;
  const selectedIsEmployee = watch('isEmployee');
  const isEmployeeError = formState.errors.isEmployee;
  const selectedLocation = watch('location');
  const locationError = formState.errors.location?.address;
  const selectedContactName = watch('contactName');
  const contactNameError = formState.errors.contactName;
  const selectedContactPhoneWithPrefix = watch('contactPhoneWithPrefix');
  const contactPhoneWithPrefixError = formState.errors.contactPhoneWithPrefix;
  const workDetails = useMemo(() => {
    if (!isCompanySelected || !isWorkTemplateSelected) {
      return undefined;
    }
    const fields = [
      {
        name: 'Pauze',
        value: selectedBreakMinutes,
        error: breakMinutesError,
      },
      {
        name: 'Aantal plekken',
        value: selectedMaxClaimants,
        error: maxClaimantsError,
      },
      ...(hideLimitedCompanyShiftFields
        ? []
        : [
            {
              name: 'Dienstverband',
              value: selectedIsFreelance || selectedIsTempwork || selectedIsEmployee,
              error: isFreelanceError || isTempworkError || isEmployeeError,
            },
          ]),
      ...(hideLimitedCompanyShiftFields
        ? []
        : [
            {
              name: "Tarief zzp'er",
              value: selectedHourlyRate || !selectedIsFreelance,
              error: hourlyRateError,
            },
          ]),
      {
        name: 'Annuleringstermijn',
        value: selectedMutableUntilHours || !selectedIsFreelance,
        error: mutableUntilHoursError,
      },
      {
        name: 'Locatie',
        value: selectedLocation,
        error: locationError,
      },
      {
        name: 'Contactpersoon',
        value: selectedContactName,
        error: contactNameError,
      },
      {
        name: 'Telefoon contactpersoon',
        value: selectedContactPhoneWithPrefix,
        error: contactPhoneWithPrefixError,
      },
    ];
    return getFieldsWarning(fields);
  }, [
    isCompanySelected,
    isWorkTemplateSelected,
    selectedBreakMinutes,
    selectedMaxClaimants,
    selectedIsFreelance,
    selectedHourlyRate,
    selectedMutableUntilHours,
    selectedIsTempwork,
    selectedIsEmployee,
    selectedLocation,
    selectedContactName,
    selectedContactPhoneWithPrefix,
    breakMinutesError,
    maxClaimantsError,
    isFreelanceError,
    hourlyRateError,
    mutableUntilHoursError,
    isTempworkError,
    isEmployeeError,
    locationError,
    contactNameError,
    contactPhoneWithPrefixError,
    hideLimitedCompanyShiftFields,
  ]);

  // no required fields in flexpool
  const flexpool = undefined;

  const selectedImageUrl = watch('imageUrl');
  const imageUrlError = formState.errors.imageUrl;
  const selectedDescriptionShort = watch('shortDescription');
  const shortDescriptionError = formState.errors.shortDescription;
  const selectedDescriptionLong = watch('longDescription');
  const longDescriptionError = formState.errors.longDescription;
  const selectedDescriptionLawRequirements = watch('descriptionLegalRequirements');
  const descriptionLawRequirementsError = formState.errors.descriptionLegalRequirements;
  const description = useMemo(() => {
    if (!isCompanySelected || !isWorkTemplateSelected) {
      return undefined;
    }
    const fields = [
      {
        name: 'Korte omschrijving',
        value: selectedDescriptionShort,
        error: shortDescriptionError,
      },
      {
        name: 'Lange omschrijving',
        value: selectedDescriptionLong,
        error: longDescriptionError,
      },
      ...(hideLimitedCompanyShiftFields
        ? []
        : [
            {
              name: 'Wettelijke vereisten',
              value: selectedDescriptionLawRequirements,
              error: descriptionLawRequirementsError,
            },
          ]),
    ];
    return getFieldsWarning(fields);
  }, [
    isCompanySelected,
    isWorkTemplateSelected,
    selectedImageUrl,
    selectedDescriptionShort,
    selectedDescriptionLong,
    selectedDescriptionLawRequirements,
    imageUrlError,
    shortDescriptionError,
    longDescriptionError,
    descriptionLawRequirementsError,
    hideLimitedCompanyShiftFields,
  ]);

  // no required fields in certificates
  const certificates = undefined;

  return { workInfo, workDetails, flexpool, description, certificates };
};
